import React, { useEffect, useRef, useState } from "react";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Pencil,
  RotateCcw,
} from "lucide-react";
import { sanitizeInlineHtml } from "./engine";

// ============================================================
// EditOverlay — builder-only direct manipulation (drag-resize)
// ============================================================
//
// Mounted by PopupRenderer ONLY when the builder's preview bridge sets
// editMode (stores can never enter it — the flag only travels over the
// flowapt:preview-settings postMessage the flowIQ editor sends).
//
// It finds the popup's resizable elements by their `data-fp-edit` markers:
//   "image"     — the layout image section (width; % of the popup row)
//   "textimage" — the per-step artwork (width; % of the content column)
//   "inputs"    — any form field wrapper (width % + height px; the model has
//                 ONE InputsStyle, so ALL fields resize together as a group)
//   "button"    — the submit button (width % + height px)
// draws hover/selection outlines + corner drag handles over them, and turns
// drags into design values:
//   - live: targets get the SAME fp-* class + CSS var the renderer would
//     apply, so the popup reflows in real time with zero round-trips;
//   - on release: the final values are posted to the parent editor as a
//     { type: "flowapt:design-edit", patch } message. The editor merges the
//     patch into the draft and re-posts settings — the re-render lands on
//     exactly the state the drag left behind, so nothing jumps.
//
// Desktop vs phone is decided by the iframe's own viewport (min-width:768px),
// which the editor's device toggle controls — dragging in the mobile preview
// writes the *Mobile* fields, desktop preview writes the desktop ones.
//
// Everything here is styled INLINE (no utility classes) so the widget shell's
// rem→px !important block can't interfere with the overlay chrome.
//
// Direct manipulation beyond resizing (designer Phase 2, 7 Oct 2026):
//   - double-click a copy element → it becomes contenteditable, Enter / blur
//     commits the sanitised HTML as flowapt:content-edit (heading, sub_heading,
//     footer_text, message), Escape restores; the button and dismiss link edit
//     through a floating input (button_text; dismiss.text is a design field);
//   - drag a form field up or down → flowapt:content-edit { field_order };
//   - drag the layout image towards a side of the panel → image.position;
//   - right-click → a menu with the same actions plus hide / move / reset.

type ResizeKey =
  | "image"
  | "textimage"
  | "inputs"
  | "button"
  | "panel"
  | "content";
/** Copy elements: a corner drag changes their FONT SIZE (sizePx on desktop,
 *  mobileSizePx in the phone preview), not their box width. */
type TextKey = "heading" | "subheading" | "body" | "footer" | "code";
/** Select-only targets: clickable for sidebar navigation, no resize.
 *  `dismiss` is select-only AND align-less: the design model has no
 *  dismiss alignment field (its old align buttons wrote to nowhere). */
type NavOnlyKey = "dismiss";
/** The welcome / success brand mark: a corner drag sets its width in px,
 *  which lives in CONTENT (content_settings.<step>.logo_width), so it travels
 *  as a separate flowapt:content-edit message, not a design patch. */
type LogoKey = "logo";
type EditKey = ResizeKey | TextKey | LogoKey | NavOnlyKey;
type Align = "left" | "center" | "right";
type Step = "welcome" | "success";

const NAV_ONLY = new Set<string>(["dismiss"]);
const TEXT_KEYS = new Set<string>(["heading", "subheading", "body", "footer", "code"]);
const isTextKey = (key: string): key is TextKey => TEXT_KEYS.has(key);

/** Font-size bounds per copy element (match the Design panel's px inputs). */
export const TEXT_MIN_PX = 8;
export const TEXT_MAX_PX: Record<TextKey, number> = {
  heading: 120,
  subheading: 60,
  body: 60,
  footer: 40,
  code: 60,
};
/** Logo width bounds (match the Content panel's logo width input). */
export const LOGO_MIN_PX = 24;
export const LOGO_MAX_PX = 600;

export interface DesignEditPatch {
  image?: {
    customWidthPct?: number;
    customMobileWidthPct?: number;
    /** Dock side after an image drag (layout images only). */
    position?: "left" | "right" | "top" | "bottom";
    /** Context menu → Hide image. */
    enabled?: boolean;
  };
  /** The dismiss link's own words / visibility (a design field, not content). */
  dismiss?: { text?: string; enabled?: boolean };
  /** Panel edge-drag (modal only, desktop only): exact px. 0 = reset.
   *  contentMaxWidthPx is the same idea one level in — the centered content
   *  column's cap, which is what owns the white space beside the text. */
  size?: { maxWidthPx?: number; contentMaxWidthPx?: number };
  steps?: {
    [step in Step]?: {
      textImage?: {
        widthPct?: number;
        mobileWidthPct?: number;
        align?: Align;
        mobileAlign?: Align;
      };
      /** On the thank-you page every element edit is a STEP OVERRIDE (size,
       *  alignment, copy sizes), so the form page keeps its own values. */
      elements?: { [k in TextKey]?: TextPatch } & { inputs?: SizePatch; button?: SizePatch };
    };
  };
  elements?: {
    inputs?: SizePatch;
    button?: SizePatch;
    heading?: TextPatch;
    subheading?: TextPatch;
    body?: TextPatch;
    footer?: TextPatch;
    /** The thank-you page's code box: its label's alignment and size. */
    code?: TextPatch;
  };
}
interface AlignPatch {
  align?: Align;
  mobileAlign?: Align;
}
/** 0 = back to the S/M/L/XL preset (desktop) or the automatic scale (phone). */
interface TextSizePatch {
  sizePx?: number;
  mobileSizePx?: number;
}
type TextPatch = AlignPatch & TextSizePatch;

/** Content-side edit (logo width) posted as flowapt:content-edit.
 *  0 = reset (desktop: the default 96px; phone: same as desktop). */
export interface ContentEditMessage {
  type: "flowapt:content-edit";
  step: Step;
  patch: {
    logo_width?: number;
    logo_mobile_width?: number;
    /** Copy edited in place (sanitised inline HTML). */
    heading?: string;
    sub_heading?: string;
    footer_text?: string;
    message?: string;
    button_text?: string;
    /** Field order after a drag; keys the step does not show are kept by the editor. */
    field_order?: string[];
    /** Context menu → Hide this field (the editor merges `enabled` into the field). */
    name_field?: { enabled: boolean };
    email_field?: { enabled: boolean };
    whatsapp_field?: { enabled: boolean };
    /** The code box's label typed in place (the editor merges it into the box's settings). */
    discount_code?: { label?: string };
  };
}

/** Font size after a corner drag. Proportional to how far the corner moved
 *  relative to the element's box (both axes count, outward = bigger), so a
 *  large heading and a small footer feel the same under the pointer. */
export const scaledTextPx = (
  startPx: number,
  startW: number,
  startH: number,
  dx: number,
  dy: number,
  signX: 1 | -1,
  signY: 1 | -1,
  maxPx: number
): number => {
  const grow = (signX * dx + signY * dy) / Math.max(1, startW + startH);
  return Math.min(maxPx, Math.max(TEXT_MIN_PX, Math.round(startPx * (1 + grow))));
};

/** Where a text-size change lands: the root element style on the form (and
 *  the intro, which wears the form's design), a step override on success. */
export const textSizePatch = (
  key: TextKey,
  step: Step,
  mobile: boolean,
  px: number
): DesignEditPatch => {
  const field: TextSizePatch = mobile ? { mobileSizePx: px } : { sizePx: px };
  return step === "success"
    ? { steps: { success: { elements: { [key]: field } } } }
    : { elements: { [key]: field } };
};
interface SizePatch extends AlignPatch {
  customWidthPct?: number;
  customMobileWidthPct?: number;
  customHeightPx?: number;
  customMobileHeightPx?: number;
}

/** Everything except the layout image and the logo can be aligned (text
 *  elements get text-align, the rest box alignment) — per device. */
type AlignableKey = Exclude<EditKey, "image" | "logo">;

interface EditOverlayProps {
  panelRef: React.RefObject<HTMLDivElement>;
  currentStep: Step;
  /** Current alignments per target (toolbar active state). `m` is the
   *  RESOLVED phone alignment (mobileAlign || align). Partial: targets with
   *  no alignment concept (dismiss) simply have no entry. */
  aligns: Partial<Record<AlignableKey, { d: Align; m: Align }>>;
}

interface KindConfig {
  /** width [class, var] per device */
  wD: [string, string];
  wM: [string, string];
  /** height [class, var] per device — only kinds with a height drag */
  hD?: [string, string];
  hM?: [string, string];
  /** all elements sharing the key resize together */
  group: boolean;
  /** align class prefix (`${prefix}-left|center|right`) when alignable */
  alignPrefix?: string;
}

const KINDS: Record<ResizeKey, KindConfig> = {
  image: {
    wD: ["fp-rz-d", "--fp-rz-d"],
    wM: ["fp-rz-m", "--fp-rz-m"],
    group: false,
  },
  // The popup panel itself (modal). PX-based (not % — there is no container
  // to be a percentage of) and desktop-only: on phones the modal is a
  // responsive 92vw clamp with no design field to write.
  panel: {
    wD: ["fp-pw", "--fp-pw"],
    wM: ["fp-pw", "--fp-pw"],
    group: false,
  },
  // The centered content column (the form/step body beside the visual). Also
  // PX-based — it maps to design size.contentMaxWidth, which is px — and like
  // the panel it is centered, so one edge moves the width at 2x the pointer.
  // Both steps share ONE cap in the model, so the group flag keeps welcome and
  // success in lockstep when both are mounted.
  content: {
    wD: ["fp-cw", "--fp-cw"],
    wM: ["fp-cw", "--fp-cw"],
    group: true,
  },
  textimage: {
    wD: ["fp-ti-d", "--fp-ti-d"],
    wM: ["fp-ti-m", "--fp-ti-m"],
    group: false,
    alignPrefix: "fp-ti",
  },
  inputs: {
    wD: ["fp-fld-w-d", "--fp-fld-w-d"],
    wM: ["fp-fld-w-m", "--fp-fld-w-m"],
    hD: ["fp-fld-h-d", "--fp-fld-h-d"],
    hM: ["fp-fld-h-m", "--fp-fld-h-m"],
    group: true,
    alignPrefix: "fp-fld",
  },
  button: {
    wD: ["fp-fld-w-d", "--fp-fld-w-d"],
    wM: ["fp-fld-w-m", "--fp-fld-w-m"],
    hD: ["fp-fld-h-d", "--fp-fld-h-d"],
    hM: ["fp-fld-h-m", "--fp-fld-h-m"],
    group: false,
    alignPrefix: "fp-fld",
  },
};

interface TargetInfo {
  id: string; // `${key}#${index}` — fields share a key but select individually
  key: EditKey;
  el: HTMLElement;
  rect: { top: number; left: number; width: number; height: number };
  /** data-fp-edit-pos (layout image only): top/bottom center on resize. */
  pos?: string;
}

const MIN_PCT = 10;
const MAX_PCT = 100;
const MIN_H = 28;
const MAX_H = 96;
/** Panel edge-drag bounds (px). Floor matches the renderer's 280px clamp. */
const MIN_PANEL_PX = 280;
const MAX_PANEL_PX = 1200;
/** Content-column bounds (px). Ceiling matches the Design panel's own
 *  "Content width (px)" input (max 1100) so drag and field agree. */
const MIN_CONTENT_PX = 200;
const MAX_CONTENT_PX = 1100;
/** The px-based kinds: centered, so an edge drag moves width at 2x the
 *  pointer, and `pct` carries PX rather than a percentage. */
const PX_KINDS = new Set<string>(["panel", "content"]);
/** Axis must move this many px before the drag commits that dimension. */
const AXIS_EPS = 4;

/**
 * Every element the overlay can select, for a given popup panel.
 *
 * The subtlety this exists to encode: `querySelectorAll` matches DESCENDANTS
 * ONLY, never the node it is called on. The panel div carries BOTH
 * `ref={panelRef}` and `data-fp-edit="panel"` (PopupRenderer), so a plain
 * `panel.querySelectorAll("[data-fp-edit]")` could never see it — which is why
 * modal edge-drag shipped fully wired end to end (KINDS.panel, the div.fp-pw
 * rule, the size.maxWidthPx patch handled on both the widget and editor sides)
 * and still could not be selected by anyone. Same trap would hit any future
 * root-level target.
 *
 * ORDER IS LOAD-BEARING: the root comes FIRST. Hit-boxes render in array order
 * at a single zIndex, so later siblings paint on top — the panel must sit UNDER
 * its children, or every click would select the panel instead of the element
 * actually under the cursor. Descendants keep document order.
 */
export const editTargetElements = (panel: HTMLElement): HTMLElement[] => [
  ...(panel.matches("[data-fp-edit]") ? [panel] : []),
  ...Array.from(panel.querySelectorAll<HTMLElement>("[data-fp-edit]")),
];

/** The overlay id to select for an editor-side target kind: the first tracked
 *  target of that kind, or null (clear) for anything else. */
export const selectIdForTarget = (
  targets: ReadonlyArray<{ id: string; key: string }>,
  target: unknown
): string | null => {
  if (typeof target !== "string") return null;
  return targets.find((t) => t.key === target)?.id ?? null;
};

// ---- direct-manipulation helpers (pure, unit-tested) ----

export type FieldKey = "name" | "email" | "whatsapp" | "birthday" | "choices";
export type ImageSide = "left" | "right" | "top" | "bottom";
type Rect = { top: number; left: number; width: number; height: number };

/** `key` moved to index `to` within `order` (unique keys). */
export const reorderFields = (order: readonly string[], key: string, to: number): string[] => {
  const rest = order.filter((k) => k !== key);
  const i = Math.max(0, Math.min(rest.length, to));
  return [...rest.slice(0, i), key, ...rest.slice(i)];
};

/** Field blocks in DOM order; consecutive wrappers with the same key (the
 *  choice fields) are one block, because field_order moves them together. */
export const fieldBlocks = (
  els: ReadonlyArray<{ key: string; rect: Rect }>
): { key: string; top: number; bottom: number; left: number; width: number }[] => {
  const out: { key: string; top: number; bottom: number; left: number; width: number }[] = [];
  for (const e of els) {
    const last = out[out.length - 1];
    if (last && last.key === e.key) {
      last.top = Math.min(last.top, e.rect.top);
      last.bottom = Math.max(last.bottom, e.rect.top + e.rect.height);
      continue;
    }
    out.push({ key: e.key, top: e.rect.top, bottom: e.rect.top + e.rect.height, left: e.rect.left, width: e.rect.width });
  }
  return out;
};

/** The slot (0..n) a pointer at `y` drops into, by block midpoints. */
export const dropSlot = (blocks: ReadonlyArray<{ top: number; bottom: number }>, y: number): number => {
  for (let i = 0; i < blocks.length; i++) {
    if (y < (blocks[i].top + blocks[i].bottom) / 2) return i;
  }
  return blocks.length;
};

/** Which side of the panel a dragged image docks to: the axis the pointer
 *  is furthest along, measured from the panel's centre. */
export const imageSideFor = (panel: Rect, x: number, y: number): ImageSide => {
  const nx = (x - (panel.left + panel.width / 2)) / Math.max(1, panel.width / 2);
  const ny = (y - (panel.top + panel.height / 2)) / Math.max(1, panel.height / 2);
  return Math.abs(nx) >= Math.abs(ny) ? (nx < 0 ? "left" : "right") : ny < 0 ? "top" : "bottom";
};

/** The content key a copy element writes on a step; null = that step has no
 *  such copy (the welcome step has no message). */
export const contentKeyForText = (
  key: string,
  step: Step
): "heading" | "sub_heading" | "footer_text" | "message" | "discount_code.label" | null =>
  key === "heading"
    ? "heading"
    : key === "subheading"
      ? "sub_heading"
      : key === "footer"
        ? "footer_text"
        : key === "body" && step === "success"
          ? "message"
          : key === "code" && step === "success"
            ? "discount_code.label"
            : null;

/** innerHTML → stored copy: drop the renderer's ™ wrappers (they would nest
 *  on every round trip), then the shared inline sanitiser. */
export const htmlForStorage = (el: HTMLElement): string => {
  const clone = el.cloneNode(true) as HTMLElement;
  clone.querySelectorAll(".fp-tm").forEach((span) =>
    span.replaceWith(document.createTextNode(span.textContent ?? ""))
  );
  return sanitizeInlineHtml(clone.innerHTML).trim();
};

const isMobileViewport = () =>
  !window.matchMedia("(min-width: 768px)").matches;

/** Width base = the containing block the CSS % resolves against. */
const containerWidth = (el: HTMLElement): number => {
  let parent = el.parentElement;
  // <picture class="contents"> has no box of its own — climb past it.
  while (parent && parent.tagName === "PICTURE") parent = parent.parentElement;
  return parent?.getBoundingClientRect().width || el.getBoundingClientRect().width || 1;
};

/** Tells the builder which target got selected so it can reveal the matching
 *  sidebar section (click-to-navigate). Fired for EVERY selection. */
const postSelect = (key: EditKey) => {
  try {
    window.parent.postMessage({ type: "flowapt:design-select", target: key }, "*");
  } catch {
    /* no parent (dev page) — ignore */
  }
};

const widthPatch = (key: EditKey, mobile: boolean, pct: number): DesignEditPatch => {
  switch (key) {
    case "image":
      return { image: mobile ? { customMobileWidthPct: pct } : { customWidthPct: pct } };
    case "inputs":
      return { elements: { inputs: mobile ? { customMobileWidthPct: pct } : { customWidthPct: pct } } };
    case "button":
      return { elements: { button: mobile ? { customMobileWidthPct: pct } : { customWidthPct: pct } } };
    default:
      return {}; // textimage handled with the step below
  }
};

const postEdit = (patch: DesignEditPatch) => {
  try {
    window.parent.postMessage({ type: "flowapt:design-edit", patch }, "*");
  } catch {
    /* no parent (dev page) — ignore */
  }
};

const postContentEdit = (step: Step, patch: ContentEditMessage["patch"]) => {
  try {
    const msg: ContentEditMessage = { type: "flowapt:content-edit", step, patch };
    window.parent.postMessage(msg, "*");
  } catch {
    /* no parent (dev page) — ignore */
  }
};

/** Targets that get corner handles (box resize, font size or logo width). */
const isResizable = (key: string): boolean =>
  key in KINDS || isTextKey(key) || key === "logo";

const rectsEqual = (a: TargetInfo[], b: TargetInfo[]) =>
  a.length === b.length &&
  a.every((t, i) => {
    const o = b[i];
    return (
      t.id === o.id &&
      t.el === o.el &&
      Math.abs(t.rect.top - o.rect.top) < 0.5 &&
      Math.abs(t.rect.left - o.rect.left) < 0.5 &&
      Math.abs(t.rect.width - o.rect.width) < 0.5 &&
      Math.abs(t.rect.height - o.rect.height) < 0.5
    );
  });

export const EditOverlay: React.FC<EditOverlayProps> = ({
  panelRef,
  currentStep,
  aligns,
}) => {
  const [targets, setTargets] = useState<TargetInfo[]>([]);
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dragLabel, setDragLabel] = useState<string | null>(null);
  // Phase 2: in-place editing, the floating text box, the context menu, and
  // the two move gestures (field reorder, image dock).
  const [editingId, setEditingId] = useState<string | null>(null);
  const [floatEdit, setFloatEdit] = useState<{ id: string; value: string } | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; id: string } | null>(null);
  const [reorder, setReorder] = useState<{
    id: string;
    y: number;
    slot: number;
    blocks: ReturnType<typeof fieldBlocks>;
  } | null>(null);
  const [imageMove, setImageMove] = useState<{ side: ImageSide; panel: Rect } | null>(null);
  const moveRef = useRef<{
    id: string;
    startX: number;
    startY: number;
    kind: "field" | "image";
    active: boolean;
  } | null>(null);
  const targetsRef = useRef<TargetInfo[]>([]);
  targetsRef.current = targets;
  const dragRef = useRef<{
    key: EditKey;
    els: HTMLElement[];
    mobile: boolean;
    startX: number;
    startY: number;
    startW: number;
    startH: number;
    base: number;
    signX: 1 | -1;
    signY: 1 | -1;
    pct: number;
    hPx: number;
    movedX: boolean;
    movedY: boolean;
  } | null>(null);

  // rAF rect tracker — keeps outlines glued through scroll/reflow/animation.
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const panel = panelRef.current;
      if (panel) {
        const els = editTargetElements(panel);
        const next: TargetInfo[] = [];
        const counts: Record<string, number> = {};
        els.forEach((el) => {
          const key = el.dataset.fpEdit as EditKey;
          if (!isResizable(key) && !NAV_ONLY.has(key)) return;
          // Panel width is a desktop concept (phones render a 92vw clamp
          // with no design field) — no target in the mobile preview.
          if (key === "panel" && isMobileViewport()) return;
          const r = el.getBoundingClientRect();
          const idx = (counts[key] = (counts[key] ?? -1) + 1);
          if (r.width < 8 || r.height < 8) return; // hidden on this device
          next.push({
            id: `${key}#${idx}`,
            key,
            el,
            rect: { top: r.top, left: r.left, width: r.width, height: r.height },
            pos: el.dataset.fpEditPos,
          });
        });
        setTargets((prev) => (rectsEqual(prev, next) ? prev : next));
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [panelRef]);

  // Hover + select happen on the overlay's OWN invisible hit-boxes (drawn at
  // each target's tracked rect, below), never on the popup's elements: in the
  // editor preview the inputs/button are DISABLED controls, and Chrome
  // suppresses mouse events on disabled form controls entirely — a listener
  // on the popup DOM (or window) never hears those clicks. The window
  // listeners below only handle deselection + Escape.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (dragRef.current) return;
      // Clicks on overlay chrome (hit-boxes, handles, toolbar) keep/refresh
      // the selection via their own handlers; anything else deselects.
      for (const node of e.composedPath()) {
        if (node instanceof HTMLElement && node.dataset?.fpOverlay) return;
      }
      setMenu(null);
      setSelectedId(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMenu(null);
        setSelectedId(null);
      }
    };
    window.addEventListener("mousedown", onDown, true);
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("mousedown", onDown, true);
      window.removeEventListener("keydown", onKey, true);
    };
  }, []);

  // The flowIQ editor's structure tree selects an element from OUTSIDE the
  // iframe: it posts flowapt:preview-select { target } and the overlay selects
  // the first target of that kind (null or an unknown kind clears it).
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const data = e.data as { type?: string; target?: unknown } | undefined;
      if (!data || data.type !== "flowapt:preview-select") return;
      setSelectedId(selectIdForTarget(targetsRef.current, data.target));
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  /** Every DOM element a drag on this target mutates (group = all fields). */
  const groupEls = (target: TargetInfo): HTMLElement[] => {
    if (!KINDS[target.key as ResizeKey]?.group) return [target.el];
    const panel = panelRef.current;
    if (!panel) return [target.el];
    return [
      ...panel.querySelectorAll<HTMLElement>(`[data-fp-edit="${target.key}"]`),
    ];
  };

  // Alignment is per-device: in the phone preview the toolbar shows and
  // writes the MOBILE alignment; desktop preview works on the desktop one.
  const currentAlign = (key: EditKey): Align => {
    const entry = aligns[key as AlignableKey];
    if (!entry) return "center";
    return isMobileViewport() ? entry.m : entry.d;
  };

  /** Copy (font size) and logo (width) drags: both scale ONE element and
   *  commit a px value, so they share this path instead of the % box maths.
   *  Live preview uses the renderer's own hooks (inline font-size / width on
   *  desktop, the .fp-fs-m / .fp-w-m phone rules) so release lands on exactly
   *  what the drag showed. */
  const startScaleDrag = (
    e: React.PointerEvent<HTMLDivElement>,
    target: TargetInfo,
    east: boolean,
    south: boolean
  ) => {
    const mobile = isMobileViewport();
    const el = target.el;
    const isLogo = target.key === "logo";
    const startRect = el.getBoundingClientRect();
    const startPx = isLogo
      ? Math.round(startRect.width)
      : Math.round(parseFloat(getComputedStyle(el).fontSize) || 16);
    dragRef.current = {
      key: target.key,
      els: [el],
      mobile,
      startX: e.clientX,
      startY: e.clientY,
      startW: startRect.width,
      startH: startRect.height,
      base: 1,
      signX: east ? 1 : -1,
      signY: south ? 1 : -1,
      pct: startPx, // px for these kinds
      hPx: 0,
      movedX: false,
      movedY: false,
    };
    const apply = (px: number) => {
      if (isLogo) {
        if (mobile) {
          el.classList.add("fp-w-m");
          el.style.setProperty("--fp-w-m", `${px}px`);
        } else {
          el.style.width = `${px}px`;
        }
      } else if (mobile) {
        el.classList.add("fp-fs-m");
        el.style.setProperty("--fp-fs-m", `${px}px`);
      } else {
        el.style.fontSize = `${px}px`;
      }
    };

    const handleEl = e.currentTarget;
    handleEl.setPointerCapture(e.pointerId);
    const onMove = (ev: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = ev.clientX - d.startX;
      const dy = ev.clientY - d.startY;
      if (Math.abs(dx) > AXIS_EPS) d.movedX = true;
      if (Math.abs(dy) > AXIS_EPS) d.movedY = true;
      if (isLogo) {
        // Centered (margin auto) and capped at 60% of its column by the
        // renderer, so one edge moves the width at 2x and the cap is honoured
        // here too — the badge never promises a size the render won't show.
        if (!d.movedX) return;
        const cap = Math.max(
          LOGO_MIN_PX,
          Math.min(LOGO_MAX_PX, Math.floor(containerWidth(el) * 0.6))
        );
        d.pct = Math.min(cap, Math.max(LOGO_MIN_PX, Math.round(d.startW + d.signX * dx * 2)));
      } else {
        if (!d.movedX && !d.movedY) return;
        d.pct = scaledTextPx(
          startPx,
          d.startW,
          d.startH,
          d.movedX ? dx : 0,
          d.movedY ? dy : 0,
          d.signX,
          d.signY,
          TEXT_MAX_PX[target.key as TextKey]
        );
      }
      apply(d.pct);
      setDragLabel(`${d.pct}px`);
    };
    const onUp = () => {
      const d = dragRef.current;
      dragRef.current = null;
      setDragLabel(null);
      handleEl.removeEventListener("pointermove", onMove);
      handleEl.removeEventListener("pointerup", onUp);
      handleEl.removeEventListener("pointercancel", onUp);
      if (!d || (!d.movedX && !d.movedY)) return;
      if (isLogo) {
        if (!d.movedX) return;
        postContentEdit(
          currentStep,
          d.mobile ? { logo_mobile_width: d.pct } : { logo_width: d.pct }
        );
      } else {
        postEdit(textSizePatch(target.key as TextKey, currentStep, d.mobile, d.pct));
      }
    };
    handleEl.addEventListener("pointermove", onMove);
    handleEl.addEventListener("pointerup", onUp);
    handleEl.addEventListener("pointercancel", onUp);
  };

  const startDrag = (
    e: React.PointerEvent<HTMLDivElement>,
    target: TargetInfo,
    east: boolean,
    south: boolean
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (isTextKey(target.key) || target.key === "logo") {
      startScaleDrag(e, target, east, south);
      return;
    }
    const kind = KINDS[target.key as ResizeKey];
    if (!kind) return; // nav-only targets have no handles, but belt+braces
    const mobile = isMobileViewport();
    const base = containerWidth(target.el);
    const [wCls, wVar] = mobile ? kind.wM : kind.wD;
    const hBind = mobile ? kind.hM : kind.hD;
    const startRect = target.el.getBoundingClientRect();
    const els = groupEls(target);
    dragRef.current = {
      key: target.key,
      els,
      mobile,
      startX: e.clientX,
      startY: e.clientY,
      startW: startRect.width,
      startH: startRect.height,
      base,
      signX: east ? 1 : -1,
      signY: south ? 1 : -1,
      pct: Math.round((startRect.width / base) * 100),
      hPx: Math.round(startRect.height),
      movedX: false,
      movedY: false,
    };
    // Vertical layout images center as they shrink on desktop — mirror the
    // renderer's class so the live drag matches the committed render.
    if (
      target.key === "image" &&
      !mobile &&
      (target.pos === "top" || target.pos === "bottom")
    ) {
      target.el.classList.add("fp-rz-center");
    }
    // Alignable kinds keep a deterministic placement while narrower —
    // align classes are device-scoped (-d-/-m-).
    if (kind.alignPrefix) {
      const dev = mobile ? "m" : "d";
      const alignCls = ["left", "center", "right"].map(
        (a) => `${kind.alignPrefix}-${dev}-${a}`
      );
      els.forEach((el) => {
        if (!alignCls.some((c) => el.classList.contains(c))) {
          el.classList.add(
            `${kind.alignPrefix}-${dev}-${currentAlign(target.key)}`
          );
        }
      });
    }

    const handleEl = e.currentTarget;
    handleEl.setPointerCapture(e.pointerId);
    const onMove = (ev: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = ev.clientX - d.startX;
      const dy = ev.clientY - d.startY;
      if (Math.abs(dx) > AXIS_EPS) d.movedX = true;
      if (Math.abs(dy) > AXIS_EPS) d.movedY = true;
      if (d.movedX) {
        if (PX_KINDS.has(d.key)) {
          // Centered box (modal panel, or the mx-auto content column): dragging
          // one edge grows both sides, so the width moves at 2× the pointer.
          // PX-based; `pct` holds px for these kinds.
          const w = d.startW + d.signX * dx * 2;
          const lo = d.key === "panel" ? MIN_PANEL_PX : MIN_CONTENT_PX;
          const hi = d.key === "panel" ? MAX_PANEL_PX : MAX_CONTENT_PX;
          d.pct = Math.min(hi, Math.max(lo, Math.round(w)));
          d.els.forEach((el) => {
            el.classList.add(wCls);
            el.style.setProperty(wVar, `${d.pct}px`);
          });
        } else {
          const w = d.startW + d.signX * dx;
          d.pct = Math.min(MAX_PCT, Math.max(MIN_PCT, Math.round((w / d.base) * 100)));
          d.els.forEach((el) => {
            el.classList.add(wCls);
            el.style.setProperty(wVar, `${d.pct}%`);
          });
        }
      }
      if (hBind && d.movedY) {
        const h = d.startH + d.signY * dy;
        d.hPx = Math.min(MAX_H, Math.max(MIN_H, Math.round(h)));
        d.els.forEach((el) => {
          el.classList.add(hBind[0]);
          el.style.setProperty(hBind[1], `${d.hPx}px`);
        });
      }
      setDragLabel(
        PX_KINDS.has(d.key)
          ? `${d.pct}px`
          : hBind && d.movedY
            ? `${d.pct}% × ${d.hPx}px`
            : `${d.pct}%`
      );
    };
    const onUp = () => {
      const d = dragRef.current;
      dragRef.current = null;
      setDragLabel(null);
      handleEl.removeEventListener("pointermove", onMove);
      handleEl.removeEventListener("pointerup", onUp);
      handleEl.removeEventListener("pointercancel", onUp);
      if (!d || (!d.movedX && !d.movedY)) return;
      // Build ONE patch with the committed dimensions.
      let patch: DesignEditPatch = {};
      if (d.key === "panel") {
        if (d.movedX) patch = { size: { maxWidthPx: d.pct } };
      } else if (d.key === "content") {
        if (d.movedX) patch = { size: { contentMaxWidthPx: d.pct } };
      } else if (d.key === "textimage") {
        if (d.movedX) {
          patch = {
            steps: {
              [currentStep]: {
                textImage: d.mobile ? { mobileWidthPct: d.pct } : { widthPct: d.pct },
              },
            },
          };
        }
      } else if (d.key === "image") {
        if (d.movedX) patch = widthPatch("image", d.mobile, d.pct);
      } else {
        const size: SizePatch = {};
        if (d.movedX) {
          if (d.mobile) size.customMobileWidthPct = d.pct;
          else size.customWidthPct = d.pct;
        }
        if (d.movedY) {
          if (d.mobile) size.customMobileHeightPx = d.hPx;
          else size.customHeightPx = d.hPx;
        }
        patch = elementPatch(d.key as "inputs" | "button", size);
      }
      if (Object.keys(patch).length) postEdit(patch);
    };
    handleEl.addEventListener("pointermove", onMove);
    handleEl.addEventListener("pointerup", onUp);
    handleEl.addEventListener("pointercancel", onUp);
  };

  const resetSize = (target: TargetInfo) => {
    const mobile = isMobileViewport();
    if (isTextKey(target.key)) {
      if (mobile) {
        target.el.classList.remove("fp-fs-m");
        target.el.style.removeProperty("--fp-fs-m");
      }
      postEdit(textSizePatch(target.key, currentStep, mobile, 0));
      return;
    }
    if (target.key === "logo") {
      if (mobile) {
        target.el.classList.remove("fp-w-m");
        target.el.style.removeProperty("--fp-w-m");
      }
      postContentEdit(currentStep, mobile ? { logo_mobile_width: 0 } : { logo_width: 0 });
      return;
    }
    const kind = KINDS[target.key as ResizeKey];
    if (!kind) return;
    const [wCls, wVar] = mobile ? kind.wM : kind.wD;
    const hBind = mobile ? kind.hM : kind.hD;
    groupEls(target).forEach((el) => {
      el.classList.remove(wCls);
      el.style.removeProperty(wVar);
      if (hBind) {
        el.classList.remove(hBind[0]);
        el.style.removeProperty(hBind[1]);
      }
    });
    if (target.key === "panel") {
      postEdit({ size: { maxWidthPx: 0 } });
    } else if (target.key === "content") {
      postEdit({ size: { contentMaxWidthPx: 0 } });
    } else if (target.key === "textimage") {
      postEdit({
        steps: {
          [currentStep]: {
            textImage: mobile ? { mobileWidthPct: 0 } : { widthPct: 0 },
          },
        },
      });
    } else if (target.key === "image") {
      postEdit(widthPatch("image", mobile, 0));
    } else {
      const size: SizePatch = mobile
        ? { customMobileWidthPct: 0, customMobileHeightPx: 0 }
        : { customWidthPct: 0, customHeightPx: 0 };
      postEdit(elementPatch(target.key as "inputs" | "button", size));
    }
  };

  const setAlign = (target: TargetInfo, align: Align) => {
    // phone preview writes the mobile override; desktop writes the base align
    const field = isMobileViewport() ? "mobileAlign" : "align";
    if (target.key === "textimage") {
      postEdit({ steps: { [currentStep]: { textImage: { [field]: align } } } });
    } else if (target.key !== "image" && target.key !== "logo" && target.key !== "dismiss") {
      postEdit(elementPatch(target.key as "inputs" | "button" | TextKey, { [field]: align }));
    }
  };

  /** Where an element's size / alignment patch lands: the base design on the
   *  form page, a `steps.success` override on the thank-you page, so the two
   *  pages stop sharing one value (Matt, 7 Oct 2026: "why is the button's
   *  alignment attached to the previous slide"). */
  const elementPatch = (
    key: "inputs" | "button" | TextKey,
    partial: SizePatch | TextPatch
  ): DesignEditPatch =>
    currentStep === "success"
      ? ({ steps: { success: { elements: { [key]: partial } } } } as DesignEditPatch)
      : ({ elements: { [key]: partial } } as DesignEditPatch);

  // ---- Phase 2: edit text in place ----
  const startInlineEdit = (target: TargetInfo) => {
    if (target.key === "button" || target.key === "dismiss") {
      setFloatEdit({ id: target.id, value: (target.el.textContent ?? "").trim() });
      return;
    }
    if (!isTextKey(target.key)) return;
    const contentKey = contentKeyForText(target.key, currentStep);
    if (!contentKey) return;
    const el = target.el;
    const original = el.innerHTML;
    el.setAttribute("contenteditable", "true");
    el.setAttribute("spellcheck", "false");
    el.style.outline = "none";
    el.focus();
    const sel = window.getSelection();
    if (sel) {
      const range = document.createRange();
      range.selectNodeContents(el);
      sel.removeAllRanges();
      sel.addRange(range);
    }
    setEditingId(target.id);
    const finish = (cancel: boolean) => {
      el.removeEventListener("blur", onBlur);
      el.removeEventListener("keydown", onKey);
      el.removeAttribute("contenteditable");
      el.removeAttribute("spellcheck");
      el.style.removeProperty("outline");
      setEditingId(null);
      if (cancel) {
        el.innerHTML = original;
        return;
      }
      const html = htmlForStorage(el);
      if (html !== sanitizeInlineHtml(original).trim()) {
        if (contentKey === "discount_code.label") postContentEdit(currentStep, { discount_code: { label: html } });
        else postContentEdit(currentStep, { [contentKey]: html });
      }
    };
    const onBlur = () => finish(false);
    const onKey = (e: KeyboardEvent) => {
      e.stopPropagation();
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        el.blur(); // → onBlur → commit
      } else if (e.key === "Escape") {
        e.preventDefault();
        finish(true);
        el.blur();
      }
    };
    el.addEventListener("blur", onBlur);
    el.addEventListener("keydown", onKey);
  };

  const commitFloatEdit = (cancel: boolean) => {
    const fe = floatEdit;
    setFloatEdit(null);
    if (!fe || cancel) return;
    const target = targetsRef.current.find((t) => t.id === fe.id);
    if (!target) return;
    const value = fe.value.trim();
    const current = (target.el.textContent ?? "").trim();
    if (!value || value === current) return;
    if (target.key === "button") postContentEdit(currentStep, { button_text: value });
    else if (target.key === "dismiss") postEdit({ dismiss: { text: value } });
  };

  // ---- Phase 2: field reorder + image dock by dragging the element itself ----
  const inputsBlocks = () =>
    fieldBlocks(
      targetsRef.current
        .filter((x) => x.key === "inputs")
        .map((x) => ({ key: x.el.dataset.fpField ?? "", rect: x.rect }))
    );
  const moveField = (target: TargetInfo, to: number) => {
    const order = inputsBlocks().map((b) => b.key).filter(Boolean);
    const key = target.el.dataset.fpField ?? "";
    if (!key) return;
    const next = reorderFields(order, key, to);
    if (next.join() !== order.join()) postContentEdit("welcome", { field_order: next });
  };
  const startMove = (e: React.PointerEvent<HTMLDivElement>, t: TargetInfo) => {
    if (e.button !== 0) return;
    const kind: "field" | "image" | null =
      t.key === "inputs" && currentStep === "welcome"
        ? "field"
        : t.key === "image" && t.pos && !isMobileViewport()
          ? "image"
          : null;
    if (!kind) return;
    const hit = e.currentTarget;
    hit.setPointerCapture(e.pointerId);
    moveRef.current = { id: t.id, startX: e.clientX, startY: e.clientY, kind, active: false };
    const onMove = (ev: PointerEvent) => {
      const m = moveRef.current;
      if (!m) return;
      if (!m.active) {
        if (Math.hypot(ev.clientX - m.startX, ev.clientY - m.startY) < 6) return;
        m.active = true;
      }
      if (m.kind === "field") {
        const blocks = inputsBlocks();
        setReorder({ id: t.id, y: ev.clientY, slot: dropSlot(blocks, ev.clientY), blocks });
      } else {
        const panel = panelRef.current?.getBoundingClientRect();
        if (!panel) return;
        const rect = { top: panel.top, left: panel.left, width: panel.width, height: panel.height };
        setImageMove({ side: imageSideFor(rect, ev.clientX, ev.clientY), panel: rect });
      }
    };
    const onUp = (ev: PointerEvent) => {
      hit.removeEventListener("pointermove", onMove);
      hit.removeEventListener("pointerup", onUp);
      hit.removeEventListener("pointercancel", onUp);
      const m = moveRef.current;
      moveRef.current = null;
      setReorder(null);
      setImageMove(null);
      if (!m || !m.active) return;
      if (m.kind === "field") {
        const blocks = inputsBlocks();
        const order = blocks.map((b) => b.key).filter(Boolean);
        const key = t.el.dataset.fpField ?? "";
        const slot = dropSlot(blocks, ev.clientY);
        const from = order.indexOf(key);
        // `slot` counts the dragged block too; index it in the list without it.
        moveField(t, slot > from ? slot - 1 : slot);
      } else {
        const panel = panelRef.current?.getBoundingClientRect();
        if (!panel) return;
        const side = imageSideFor(
          { top: panel.top, left: panel.left, width: panel.width, height: panel.height },
          ev.clientX,
          ev.clientY
        );
        if (side !== t.pos) postEdit({ image: { position: side } });
      }
    };
    hit.addEventListener("pointermove", onMove);
    hit.addEventListener("pointerup", onUp);
    hit.addEventListener("pointercancel", onUp);
  };

  // ---- Phase 2: context menu ----
  const menuItemsFor = (t: TargetInfo): { label: string; run: () => void; on?: boolean }[] => {
    const items: { label: string; run: () => void; on?: boolean }[] = [];
    const resizeCfgT = KINDS[t.key as ResizeKey];
    const canText =
      (isTextKey(t.key) && !!contentKeyForText(t.key, currentStep)) ||
      t.key === "button" ||
      t.key === "dismiss";
    if (canText) items.push({ label: "Edit text", run: () => startInlineEdit(t) });
    if (t.key === "inputs" && currentStep === "welcome") {
      const order = inputsBlocks().map((b) => b.key).filter(Boolean);
      const key = t.el.dataset.fpField ?? "";
      const i = order.indexOf(key);
      if (i > 0) items.push({ label: "Move up", run: () => moveField(t, i - 1) });
      if (i >= 0 && i < order.length - 1) items.push({ label: "Move down", run: () => moveField(t, i + 1) });
      if (key === "name" || key === "email" || key === "whatsapp" || key === "birthday") {
        items.push({
          label: "Hide this field",
          run: () => postContentEdit("welcome", { [`${key}_field`]: { enabled: false } } as ContentEditMessage["patch"]),
        });
      }
    }
    if (t.key === "image" && t.pos) {
      (["left", "right", "top", "bottom"] as const).forEach((side) =>
        items.push({
          label: `Image on the ${side}`,
          on: t.pos === side,
          run: () => {
            if (t.pos !== side) postEdit({ image: { position: side } });
          },
        })
      );
      items.push({ label: "Hide image", run: () => postEdit({ image: { enabled: false } }) });
    }
    if (t.key === "dismiss") items.push({ label: "Hide dismiss link", run: () => postEdit({ dismiss: { enabled: false } }) });
    const alignableT = resizeCfgT
      ? !!resizeCfgT.alignPrefix
      : t.key !== "image" && t.key !== "dismiss" && t.key !== "logo";
    if (alignableT) {
      (["left", "center", "right"] as const).forEach((a) =>
        items.push({ label: `Align ${a === "center" ? "centre" : a}`, on: currentAlign(t.key) === a, run: () => setAlign(t, a) })
      );
    }
    if (isResizable(t.key)) items.push({ label: "Reset size", run: () => resetSize(t) });
    items.push({ label: "Open its settings", run: () => postSelect(t.key) });
    return items;
  };

  const selected = targets.find((t) => t.id === selectedId) || null;
  const menuTarget = menu ? targets.find((t) => t.id === menu.id) || null : null;
  const floatTarget = floatEdit ? targets.find((t) => t.id === floatEdit.id) || null : null;
  const hovered =
    hoverId !== selectedId ? targets.find((t) => t.id === hoverId) : undefined;

  const badge =
    dragLabel ??
    (selected
      ? isTextKey(selected.key)
        ? `${Math.round(parseFloat(getComputedStyle(selected.el).fontSize) || 0)}px`
        : selected.key === "panel" || selected.key === "logo"
          ? `${Math.round(selected.rect.width)}px`
          : `${Math.round((selected.rect.width / containerWidth(selected.el)) * 100)}%`
      : null);

  const btnStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 22,
    height: 22,
    border: "none",
    borderRadius: 5,
    background: "transparent",
    color: "#e5e7eb",
    cursor: "pointer",
    padding: 0,
  };

  const handleBox = (
    target: TargetInfo,
    corner: "nw" | "ne" | "sw" | "se"
  ): React.ReactElement => {
    const { rect } = target;
    const east = corner === "ne" || corner === "se";
    const south = corner === "sw" || corner === "se";
    return (
      <div
        key={corner}
        data-fp-overlay="handle"
        onPointerDown={(e) => startDrag(e, target, east, south)}
        style={{
          position: "fixed",
          top: (south ? rect.top + rect.height : rect.top) - 5,
          left: (east ? rect.left + rect.width : rect.left) - 5,
          width: 10,
          height: 10,
          background: "#ffffff",
          border: "2px solid #2563eb",
          borderRadius: 3,
          pointerEvents: "auto",
          cursor: corner === "nw" || corner === "se" ? "nwse-resize" : "nesw-resize",
          zIndex: 2,
          touchAction: "none",
        }}
      />
    );
  };

  const resizeCfg = selected ? KINDS[selected.key as ResizeKey] : undefined;
  // Box kinds, copy (font size) and the logo (width) all get handles.
  const resizable = !!selected && isResizable(selected.key);
  // Text elements align; resize kinds need their prefix. The layout image
  // and the logo have no alignment, and dismiss has no align field in the
  // design model — its buttons used to post patches the editor dropped.
  const alignable =
    !!selected &&
    (resizeCfg
      ? !!resizeCfg.alignPrefix
      : selected.key !== "image" && selected.key !== "dismiss" && selected.key !== "logo");

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 2147483000,
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      {/* Invisible hit-boxes — the actual hover/select surface for every
          target (see the note above the deselect effect). Kept below the
          handles/toolbar (zIndex) so those stay clickable. */}
      {targets.map((t) => (
        <div
          key={t.id}
          data-fp-overlay="hit"
          onMouseEnter={() => {
            if (!dragRef.current) setHoverId(t.id);
          }}
          onMouseLeave={() => {
            if (!dragRef.current)
              setHoverId((h) => (h === t.id ? null : h));
          }}
          onMouseDown={() => {
            if (dragRef.current) return;
            setMenu(null);
            setSelectedId(t.id);
            postSelect(t.key); // reveal the matching sidebar section
          }}
          onPointerDown={(e) => startMove(e, t)}
          onDoubleClick={() => startInlineEdit(t)}
          onContextMenu={(e) => {
            e.preventDefault();
            setSelectedId(t.id);
            postSelect(t.key);
            setMenu({ x: e.clientX, y: e.clientY, id: t.id });
          }}
          onWheel={(e) => {
            // keep the popup scrollable while the cursor sits on a hit-box
            panelRef.current?.scrollBy({ top: e.deltaY });
          }}
          style={{
            position: "fixed",
            top: t.rect.top,
            left: t.rect.left,
            width: t.rect.width,
            height: t.rect.height,
            background: "transparent",
            // while a copy element is being typed into, clicks must reach it
            pointerEvents: editingId ? "none" : "auto",
            cursor:
              (t.key === "inputs" && currentStep === "welcome") || (t.key === "image" && t.pos)
                ? "move"
                : "pointer",
            touchAction: "none",
            zIndex: 1,
          }}
        />
      ))}

      {hovered && (
        <div
          style={{
            position: "fixed",
            top: hovered.rect.top - 1,
            left: hovered.rect.left - 1,
            width: hovered.rect.width + 2,
            height: hovered.rect.height + 2,
            border: "1.5px dashed #60a5fa",
            borderRadius: 4,
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
      )}

      {selected && (
        <>
          <div
            style={{
              position: "fixed",
              top: selected.rect.top - 1,
              left: selected.rect.left - 1,
              width: selected.rect.width + 2,
              height: selected.rect.height + 2,
              border: "2px solid #2563eb",
              borderRadius: 4,
              pointerEvents: "none",
            }}
          />
          {resizable &&
            (["nw", "ne", "sw", "se"] as const).map((c) =>
              handleBox(selected, c)
            )}

          {/* toolbar — resizable targets get badge + align + reset (copy shows
              its font size in px, the logo its width) */}
          {(resizable || alignable) && (
          <div
            data-fp-overlay="toolbar"
            style={{
              position: "fixed",
              top: Math.max(6, selected.rect.top - 34),
              left: Math.max(6, selected.rect.left),
              display: "flex",
              alignItems: "center",
              gap: 4,
              background: "#111827",
              color: "#ffffff",
              borderRadius: 999,
              padding: "4px 10px",
              fontSize: 11,
              fontWeight: 600,
              boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
              pointerEvents: "auto",
              userSelect: "none",
              whiteSpace: "nowrap",
              zIndex: 3,
            }}
          >
            {resizable && (
              <span style={{ minWidth: 34, textAlign: "center" }}>
                {badge ?? "—"}
              </span>
            )}
            {alignable && (
              <>
                {resizable && (
                  <span
                    style={{
                      width: 1,
                      height: 14,
                      background: "rgba(255,255,255,0.25)",
                    }}
                  />
                )}
                {(
                  [
                    ["left", AlignLeft],
                    ["center", AlignCenter],
                    ["right", AlignRight],
                  ] as const
                ).map(([align, Icon]) => (
                  <button
                    key={align}
                    type="button"
                    data-fp-overlay="align"
                    title={`Align ${align}`}
                    onClick={() => setAlign(selected, align)}
                    style={{
                      ...btnStyle,
                      background:
                        currentAlign(selected.key) === align
                          ? "rgba(255,255,255,0.2)"
                          : "transparent",
                      color:
                        currentAlign(selected.key) === align
                          ? "#ffffff"
                          : "#9ca3af",
                    }}
                  >
                    <Icon size={13} />
                  </button>
                ))}
              </>
            )}
            {((isTextKey(selected.key) && !!contentKeyForText(selected.key, currentStep)) ||
              selected.key === "button" ||
              selected.key === "dismiss") && (
              <button
                type="button"
                data-fp-overlay="edit"
                title="Edit text (or double-click it)"
                onClick={() => startInlineEdit(selected)}
                style={btnStyle}
              >
                <Pencil size={13} />
              </button>
            )}
            <span
              style={{
                width: 1,
                height: 14,
                background: "rgba(255,255,255,0.25)",
              }}
            />
            <button
              type="button"
              data-fp-overlay="reset"
              title="Reset to automatic size"
              onClick={() => resetSize(selected)}
              style={{ ...btnStyle, width: "auto", padding: "0 4px", gap: 3 }}
            >
              <RotateCcw size={12} />
              <span style={{ fontSize: 10 }}>Auto</span>
            </button>
          </div>
          )}
        </>
      )}

      {/* ---- Phase 2 chrome: reorder line + ghost, image dock bar, floating
          text box (button / dismiss), context menu ---- */}
      {reorder &&
        (() => {
          const b = reorder.blocks;
          if (!b.length) return null;
          const slotY =
            reorder.slot < b.length ? b[reorder.slot].top - 3 : b[b.length - 1].bottom + 3;
          const dragged = targets.find((t) => t.id === reorder.id);
          return (
            <>
              <div
                style={{
                  position: "fixed",
                  top: slotY,
                  left: b[0].left,
                  width: b[0].width,
                  height: 3,
                  background: "#2563eb",
                  borderRadius: 2,
                  pointerEvents: "none",
                  zIndex: 3,
                }}
              />
              {dragged && (
                <div
                  style={{
                    position: "fixed",
                    top: reorder.y - dragged.rect.height / 2,
                    left: dragged.rect.left,
                    width: dragged.rect.width,
                    height: dragged.rect.height,
                    background: "rgba(37,99,235,0.12)",
                    border: "1.5px solid #2563eb",
                    borderRadius: 8,
                    pointerEvents: "none",
                    zIndex: 3,
                  }}
                />
              )}
            </>
          );
        })()}
      {imageMove &&
        (() => {
          const p = imageMove.panel;
          const side = imageMove.side;
          const bar: React.CSSProperties =
            side === "left"
              ? { top: p.top, left: p.left, width: 6, height: p.height }
              : side === "right"
                ? { top: p.top, left: p.left + p.width - 6, width: 6, height: p.height }
                : side === "top"
                  ? { top: p.top, left: p.left, width: p.width, height: 6 }
                  : { top: p.top + p.height - 6, left: p.left, width: p.width, height: 6 };
          return (
            <>
              <div
                style={{
                  position: "fixed",
                  ...bar,
                  background: "#2563eb",
                  borderRadius: 3,
                  boxShadow: "0 0 0 4px rgba(37,99,235,0.25)",
                  pointerEvents: "none",
                  zIndex: 3,
                }}
              />
              <div
                style={{
                  position: "fixed",
                  top: p.top + 10,
                  left: p.left + p.width / 2,
                  transform: "translateX(-50%)",
                  background: "#111827",
                  color: "#ffffff",
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "4px 10px",
                  borderRadius: 999,
                  pointerEvents: "none",
                  zIndex: 4,
                }}
              >
                Image on the {side}
              </div>
            </>
          );
        })()}
      {floatEdit && floatTarget && (
        <input
          data-fp-overlay="edit"
          autoFocus
          value={floatEdit.value}
          onChange={(e) => setFloatEdit({ id: floatEdit.id, value: e.target.value })}
          onFocus={(e) => e.currentTarget.select()}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === "Enter") commitFloatEdit(false);
            else if (e.key === "Escape") commitFloatEdit(true);
          }}
          onBlur={() => commitFloatEdit(false)}
          style={{
            position: "fixed",
            top: floatTarget.rect.top,
            left: floatTarget.rect.left,
            width: floatTarget.rect.width,
            height: floatTarget.rect.height,
            boxSizing: "border-box",
            border: "2px solid #2563eb",
            borderRadius: 8,
            padding: "0 12px",
            fontFamily: "inherit",
            fontSize: 14,
            fontWeight: 600,
            textAlign: "center",
            color: "#111827",
            background: "#ffffff",
            outline: "none",
            pointerEvents: "auto",
            zIndex: 4,
          }}
        />
      )}
      {menu && menuTarget && (
        <div
          data-fp-overlay="menu"
          style={{
            position: "fixed",
            top: Math.min(menu.y, Math.max(8, window.innerHeight - 300)),
            left: Math.min(menu.x, Math.max(8, window.innerWidth - 214)),
            minWidth: 198,
            background: "#ffffff",
            color: "#111827",
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
            padding: 4,
            pointerEvents: "auto",
            zIndex: 5,
            fontSize: 12.5,
          }}
        >
          {menuItemsFor(menuTarget).map((it) => (
            <button
              key={it.label}
              type="button"
              data-fp-overlay="menu"
              onClick={() => {
                setMenu(null);
                it.run();
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#f3f4f6";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                width: "100%",
                border: "none",
                background: "transparent",
                textAlign: "left",
                padding: "7px 10px",
                borderRadius: 7,
                cursor: "pointer",
                font: "inherit",
                color: "inherit",
              }}
            >
              <span>{it.label}</span>
              {it.on && <span style={{ color: "#2563eb" }}>✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default EditOverlay;
